/* eslint-disable no-param-reassign */
import React, { useRef, useEffect } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import avo from 'analytics/avo';
import { isSafari } from 'react-device-detect';

import JBImage from 'components/essentials/JBImage';
import scrollTo from 'commons/scrollTo';
import isClient from 'commons/isClient';
import getHeightOfElement from 'commons/getHeightOfElement';
import getTopPositionOfElement from 'commons/getTopPositionOfElement';
import getBottomPositionOfElement from 'commons/getBottomPositionOfElement';
import notificationBannerHelper from 'components/consumer/NotificationBanner/utils';
import IconCheck from 'components/icons/iconCheck';
import IconHeart2 from 'components/icons/iconHeart2';
import IconHeart4 from 'components/icons/iconHeart4';

import useScreen from 'hooks/useScreen';
import useIsTouchDevice from 'hooks/useIsTouchDevice';
import FabricGallery from '../GridWithSidebar/components/FabricGallery';
// for requestAnimationFrame scroll for safari smoothscroll polyfill bug

const SWATCHES_BANNER_HEIGHT = 412;

// easing functions http://goo.gl/5HLl8

function easeInOutQuad(t, b, c, d) {
  t /= d / 2;
  if (t < 1) {
    return (c / 2) * t * t + b;
  }
  t -= 1;
  return (-c / 2) * (t * (t - 2) - 1) + b;
}

// requestAnimationFrame for Smart Animating http://goo.gl/sx5sts
const requestAnimFrame = (function requestAnimFrame() {
  if (!isClient()) return null;

  return (
    window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    function raf(callback) {
      window.setTimeout(callback, 1000 / 60);
    }
  );
})();

function smoothScrollToForSafari(to, callback, duration) {
  // because it's difficult to detect the scrolling element, just move them all
  function move(amount) {
    document.documentElement.scrollTop = amount;
    document.body.parentNode.scrollTop = amount;
    document.body.scrollTop = amount;
  }

  function position() {
    return (
      document.documentElement.scrollTop ||
      document.body.parentNode.scrollTop ||
      document.body.scrollTop
    );
  }
  const start = position();
  const change = to - start;
  let currentTime = 0;
  const increment = 20;
  duration = typeof duration === 'undefined' ? 500 : duration;
  const animateScroll = function animateScroll() {
    // increment the time
    currentTime += increment;
    // find the value with the quadratic in-out easing function
    const val = easeInOutQuad(currentTime, start, change, duration);
    // move the document.body
    move(val);
    // do the animation unless its over
    if (currentTime < duration) {
      requestAnimFrame(animateScroll);
    } else if (callback && typeof callback === 'function') {
      // the animation is done so lets callback
      callback();
    }
  };
  animateScroll();
}

const FabricGridItem = ({
  alignment,
  footerTitle,
  imageProps,
  isNotHovered,
  isSelected,
  onClick,
  openSidebarOnClick = false,
  title,
  isFavorited,
  setOptionAsFav,
  removeOptionAsFav,
  toggleFavClick,
  mobileItemsPerRow,
  fabricOption,
  isUserLoggedIn,
  numOfFavFabrics,
  images,
  desktopFilterSize,
}) => {
  const selectedGridItemRef = useRef(null);
  const lastTapRef = useRef(null);
  const showNotificationBannerRef = useRef(null);
  const isTouchDevice = useIsTouchDevice();

  const { isMobile } = useScreen();

  const DOUBLE_PRESS_DELAY = 300;
  let location = isClient() ? window?.location?.pathname?.toLowerCase() : '';

  if (location) {
    location = location.includes('safeguard-fabrics')
      ? 'Safeguard Fabrics Page'
      : location.includes('pet-friendly')
      ? 'Pet Friendly Page'
      : location.includes('swatches')
      ? 'Swatches'
      : null;
  }
  const favProps = {
    fabricId: fabricOption?.id,
    fabricName: footerTitle?.name,
    fabricColor: fabricOption?.color,
    location,
  };
  // start effects
  useEffect(() => {
    if (
      showNotificationBannerRef.current &&
      isFavorited &&
      numOfFavFabrics === 1
    ) {
      notificationBannerHelper.showMsg('FabricFavoritedBanner', {
        fabricName: footerTitle?.name,
        isFavorited,
        isUserLoggedIn,
        favNum: numOfFavFabrics,
      });
      showNotificationBannerRef.current = false;
    }
  }, [footerTitle?.name, isFavorited, isUserLoggedIn, numOfFavFabrics]);
  // end effects
  // fabricOption is required
  if (!fabricOption) {
    return null;
  }
  // comes here for mobile/tablet double tap handling.
  const onClickOrDoubleTap = () => {
    if (!isSelected) {
      onClick();
    } else if (
      lastTapRef?.current &&
      Date.now() - lastTapRef.current < DOUBLE_PRESS_DELAY
    ) {
      if (isFavorited && removeOptionAsFav) {
        removeOptionAsFav(fabricOption.id);
        avo.fabricUnfavorited(favProps);
      } else if (!isFavorited && setOptionAsFav) {
        setOptionAsFav(fabricOption.id);
        avo.fabricFavorited(favProps);
        showNotificationBannerRef.current = true;
      }
      lastTapRef.current = null;
    } else {
      lastTapRef.current = Date.now();
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <button
      type="button"
      className={classNames(
        'group/FabricGridItem [background:none] border-0 text-center focus-visible:outline-offset-[9px] md:mb-4',
        {
          'hover:cursor-pointer': openSidebarOnClick,
        }
      )}
      data-xc={isSelected ? 'FabricGridItem--selected' : undefined}
      ref={selectedGridItemRef}
      role={openSidebarOnClick ? 'button' : undefined}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={openSidebarOnClick ? '0' : undefined}
      onClick={
        mobileItemsPerRow !== 1 && openSidebarOnClick
          ? () => {
              onClickOrDoubleTap();
              if (!isMobile) {
                const gridWrapperElement = document.querySelector(
                  '[data-xc="FabricGrid"]'
                );
                const stickySidebarWrapperElement = document.querySelector(
                  '[data-xc="PushAcrossSidebar"]'
                );
                const stickySidebarElement = document.querySelector(
                  '[data-xc="PushAcrossSidebar__sticky"]'
                );

                setTimeout(() => {
                  const selectedElement = document.querySelector(
                    '[data-xc="FabricGridItem--selected"]'
                  );

                  if (
                    gridWrapperElement &&
                    stickySidebarWrapperElement &&
                    stickySidebarElement &&
                    selectedElement
                  ) {
                    const elementHeight = getHeightOfElement(selectedElement);
                    const topOfElement = getTopPositionOfElement(
                      selectedElement
                    );
                    const bottomOfElement = getBottomPositionOfElement(
                      selectedElement
                    );

                    const topOfSidebar = getTopPositionOfElement(
                      stickySidebarWrapperElement
                    );
                    const sidebarHeight = getHeightOfElement(
                      stickySidebarElement
                    );

                    const bottomOfGrid = getBottomPositionOfElement(
                      gridWrapperElement
                    );

                    const isInBottomRow =
                      bottomOfGrid - bottomOfElement < elementHeight;

                    const isInBottomTwoRows =
                      bottomOfGrid - bottomOfElement < elementHeight * 2;

                    // for requestAnimationFrame scroll for Safari
                    const bodyRect = document.body.getBoundingClientRect();
                    const elemRect = selectedElement.getBoundingClientRect();
                    const offset = elemRect.top - (bodyRect.top + 200);

                    if (
                      bottomOfElement <=
                      topOfSidebar + sidebarHeight / 2 + elementHeight
                    ) {
                      scrollTo({
                        top:
                          topOfElement -
                          32 -
                          60 -
                          desktopFilterSize -
                          SWATCHES_BANNER_HEIGHT,
                        left: 0,
                        behavior: 'smooth',
                      });
                    } else if (isInBottomRow) {
                      scrollTo({
                        top:
                          topOfElement -
                          (sidebarHeight - elementHeight - 24) -
                          60 -
                          desktopFilterSize -
                          SWATCHES_BANNER_HEIGHT,
                        left: 0,
                        behavior: 'smooth',
                      });
                    } else if (isInBottomTwoRows) {
                      scrollTo({
                        top:
                          topOfElement -
                          (sidebarHeight - 2 * (elementHeight + 24)) -
                          60 -
                          desktopFilterSize -
                          SWATCHES_BANNER_HEIGHT,
                        left: 0,
                        behavior: 'smooth',
                      });
                    } else if (isSafari) {
                      if (isSafari) {
                        smoothScrollToForSafari(offset, null, 300);
                      }
                    } else {
                      selectedElement.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                      });
                    }
                  }
                }, 200);
              }
            }
          : () => {
              if (isTouchDevice && toggleFavClick) {
                // comes here for pet-friendly and safeguard-fabrics page.
                // double click doesnt work in mobile .so using double (single clicks ).
                if (
                  lastTapRef.current &&
                  Date.now() - lastTapRef.current < DOUBLE_PRESS_DELAY
                ) {
                  toggleFavClick(favProps);
                  showNotificationBannerRef.current = true;
                } else {
                  lastTapRef.current = Date.now();
                }
              }
            }
      }
      onDoubleClick={() => {
        if (openSidebarOnClick || !toggleFavClick) {
          return;
        }

        toggleFavClick(favProps);
        showNotificationBannerRef.current = true;
      }}
    >
      <div className="relative [&_img]:align-middle">
        {mobileItemsPerRow === 1 ? (
          <div style={{ position: 'relative' }}>
            <FabricGallery
              slides={images}
              isMobileMaterialSwatches
              bottomRightSide={
                <button
                  type="button"
                  className={classNames(
                    '![background:none] !border-0 !rounded-full !flex !min-h-[34px] !min-w-[34px] !mr-0',
                    'focus:!outline-0',
                    'focus-visible:outline-offset-8',
                    'hover:cursor-pointer',
                    '[&_svg]:!h-8 [&_svg]:!w-8',
                    '[&_svg_path]:!fill-white',
                    '[&:hover_svg_path]:stroke-[#107c8c]',
                    {
                      '[&:hover_svg_path]:!fill-brand': !isFavorited,
                    }
                  )}
                  onClick={e => {
                    e.stopPropagation();

                    toggleFavClick(favProps);
                    if (!isFavorited) {
                      showNotificationBannerRef.current = true;
                    }
                  }}
                >
                  {isFavorited ? (
                    <IconHeart2 fill="white" color="white" />
                  ) : (
                    <IconHeart4 />
                  )}
                </button>
              }
            />
          </div>
        ) : imageProps ? (
          <div
            className={classNames(
              'w-full object-cover bg-gray-light8 pt-[100%] relative transition-[all_.25s_ease-in-out] md:mb-2 hover:scale-105',
              {
                'FabricGridItem__inner-container--selected': isSelected,
              }
            )}
          >
            <JBImage
              {...imageProps}
              className={classNames(
                'h-full left-0 absolute top-0 [transition:all_.25s_ease-in-out] w-full md:rounded',
                {
                  'shadow-[0_0_0_3px_#fff,0_0_0_5px_#262626]': isSelected,
                }
              )}
              height={171}
              width={171}
            />

            {isSelected && (
              <div className="items-center bg-gray-dark flex h-8 justify-center absolute right-[-5px] top-[-5px] w-8 md:rounded-tr [&_svg]:h-4 [&_svg]:w-4 [&_svg_path]:fill-white [&_svg_path]:stroke-[#fff]">
                <IconCheck />
              </div>
            )}

            {isFavorited && (
              <div className="!bottom-2 right-2 [&_svg]:!mr-0 absolute bottom-2.5 w-full [&_svg]:w-6 [&_svg]:h-6 [&_svg]:relative [&_svg]:z-[999] [&_svg]:float-right [&_svg]:mr-[15px]">
                <IconHeart2 fill="white" color="white" />
              </div>
            )}
          </div>
        ) : (
          <div className="w-full object-cover bg-gray-light8 pt-[100%] relative transition-[all_.25s_ease-in-out] md:mb-2 hover:scale-105" />
        )}

        {footerTitle && (
          <div
            role="presentation"
            className={classNames(
              'font-sans font-bold mx-0 my-2 max-[767px]:hidden min-[1440px]:hidden',
              {
                'max-[767px]:!text-gray max-[767px]:!block max-[767px]:!text-sm max-[767px]:mt-3 max-[767px]:mx-4 max-[767px]:mb-6 max-[767px]:text-left':
                  mobileItemsPerRow === 1,
              }
            )}
            style={footerTitle.style}
            onClick={
              openSidebarOnClick
                ? () => {
                    onClickOrDoubleTap();
                  }
                : () => {
                    if (isTouchDevice && toggleFavClick) {
                      // comes here for pet-friendly and safeguard-fabrics page.
                      // double click doesnt work in mobile .so using double (single clicks ).
                      if (
                        lastTapRef.current &&
                        Date.now() - lastTapRef.current < DOUBLE_PRESS_DELAY
                      ) {
                        toggleFavClick(favProps);
                        showNotificationBannerRef.current = true;
                      } else {
                        lastTapRef.current = Date.now();
                      }
                    }
                  }
            }
          >
            {footerTitle.name}
          </div>
        )}

        {alignment && (
          <div
            className={classNames('w-4/5 max-[767px]:hidden', {
              'min-[1440px]:opacity-0 min-[1440px]:[transition:all_.5s]': !isNotHovered,
              'group-hover/FabricGridItem:opacity-100':
                !isNotHovered && openSidebarOnClick,
              'max-[767px]:!text-gray max-[767px]:!block max-[767px]:!text-sm max-[767px]:mt-3 max-[767px]:mx-4 max-[767px]:mb-6 max-[767px]:text-left':
                mobileItemsPerRow === 1,
              'text-left absolute left-0 top-0 mt-[5%] ml-[5%]':
                alignment === 'topleft',
              'text-left absolute right-0 top-0 mt-[5%] mr-[5%]':
                alignment === 'topright',
              'text-left absolute left-0 bottom-0 mb-[5%] ml-[5%]':
                alignment === 'bottomleft',
              'text-left absolute right-0 bottom-0 mb-[5%] mr-[5%]':
                alignment === 'bottomright',
              'text-left absolute top-1/2 left-[30px] transform-[translateY(-50%)]':
                alignment === 'left',
              'text-left absolute top-1/2 right-0 transform-[translate(-50%,50%)]':
                alignment === 'right',
              'min-[1440px]:text-center min-[1440px]:absolute min-[1440px]:top-1/2 min-[1440px]:left-1/2 min-[1440px]:transform-[translate(-50%,-50%)]':
                alignment === 'middle',
              'text-center mt-[10%] absolute top-0 left-1/2 transform-[translate(-50%,-50%)]':
                alignment === 'top',
              'top-0 mt-[5%] absolute left-1/2 transform-[translate(-50%)]':
                alignment === 'topmiddle',
              'absolute left-1/2 transform-[translate(-50%)] bottom-0 mb-[5%]':
                alignment === 'bottommiddle',
            })}
          >
            {title && (
              <h4
                className="font-sans font-bold mx-0 my-2 min-[1440px]:mb-0"
                style={{
                  lineHeight: '1.1',
                  marginBottom: '10px',
                  maxWidth: '700px',
                  ...title.style,
                }}
              >
                {title.name}
              </h4>
            )}
          </div>
        )}
      </div>

      {footerTitle && (
        <div className="max-[1439px]:hidden text-sm">{footerTitle.name}</div>
      )}
    </button>
  );
};

FabricGridItem.propTypes = {
  alignment: PropTypes.string,
  footerTitle: PropTypes.object,
  imageProps: PropTypes.object,
  isNotHovered: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  openSidebarOnClick: PropTypes.bool,
  title: PropTypes.string,
  isFavorited: PropTypes.bool,
  setOptionAsFav: PropTypes.func,
  removeOptionAsFav: PropTypes.func,
  toggleFavClick: PropTypes.func,
  hoveredOptionId: PropTypes.number,
  fabricOption: PropTypes.object,
  isUserLoggedIn: PropTypes.bool,
  numOfFavFabrics: PropTypes.number,
  mobileItemsPerRow: PropTypes.number,
  images: PropTypes.array,
  desktopFilterSize: PropTypes.number,
};

export default FabricGridItem;
